import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import style from "./ContinueHero.module.css";
import Icon from "@/ui/icon/Icon";
import Container from "@/layouts/container/Container";
import CourseProgressBanner from "@/features/courses/components/courseProgressBanner/CourseProgressBanner";
import { useGetContinueWatching } from "@/features/courses/hooks/useGetContinueWatching";
import { useFreeCourses } from "@/features/courses/hooks/useFreeCourses";

/**
 * The hero on a free account's home page.
 *
 * The banner itself is the one production has always shown a signed-in user —
 * the same CourseProgressBanner in the same band that SubscriberHome uses — so
 * a free account sees what it saw before this page existed. What this branch
 * added and keeps is the wrapping: the pending guard that stops the hero
 * painting twice, and the welcome state below for an account with nothing to
 * continue, which production answered with an empty page and no h1.
 */

function ContinueHero() {
	const { t } = useTranslation();
	const { data, isPending } = useGetContinueWatching();
	const { freeOffer } = useFreeCourses();
	const video = data?.video;

	const course = video?.module?.course;

	// While the request is in flight `video` is still undefined, so the
	// fallback below would paint and then be swapped for the real
	// continue-watching hero — a visible heading-and-content flash on every
	// load. Paint nothing until the answer is known.
	if (isPending) return null;

	// Without every slug the unit URL would come out as
	// /courses/undefined/undefined/<slug>, so treat a partial payload the same
	// as no payload at all.
	if (!video || !video.module?.slug || !course?.slug) {
		// A brand-new registrant has nothing to continue. Returning null left
		// the page with no hero and no h1 at all — for the most convertible
		// user on the site.
		return (
			<section className={style.hero}>
				<div className={`${style.inner} ${style.innerSolo}`}>
					<div className={style.copy}>
						<div className={style.eyebrow}>
							{t("landing:welcome.eyebrow")}
						</div>
						<h1 className={style.title}>
							{t("landing:welcome.title")}
						</h1>
						<p className={style.desc}>
							{t("landing:welcome.desc", { offer: freeOffer })}
						</p>
						<Link to="/courses" className={style.cta}>
							<Icon icon="fa-compass" />
							{t("landing:welcome.browse")}
						</Link>
					</div>
				</div>
			</section>
		);
	}

	return (
		<div className={style.band}>
			<Container>
				<CourseProgressBanner courseData={data} />
			</Container>
		</div>
	);
}

export default ContinueHero;
